import type { SyllabusContentOverridePath } from '@dosfilos/application/composition';
import { callable } from '../callable';

/** S5 — vista previa de volver a aplicar la plantilla (ver syllabus-session-plan). */
export type RegenProtectReason =
  | 'attendance'
  | 'last_on_attendance_day'
  | 'into_attendance_day'
  | 'date_moved'
  | 'topic_edited'
  | 'module_moved'
  | 'removed';
export interface RegenSlot {
  date: string;
  startTime: string;
  endTime: string;
  topic: string;
}
export interface SyllabusRegenerationPreviewDto {
  hasSyllabus: boolean;
  summary: {
    replace: number;
    add: number;
    remove: number;
    removeOnAttendanceDay: number;
    lockedOnAttendanceDay: number;
    protectedByAttendance: number;
    protectedByEdit: number;
    skippedOnAttendanceDate: number;
    keyTextsCleared: number;
    keyTextsOnAttendanceDay: number;
    moved: number;
    manual: number;
  };
  changes: Array<{
    from: RegenSlot;
    to: RegenSlot;
    moved: boolean;
    topicChanged: boolean;
    keyTextsCleared: boolean;
    keyTextsChanged: boolean;
    protect: RegenProtectReason[];
  }>;
  removals: Array<{ slot: RegenSlot; protect: RegenProtectReason[] }>;
  /** Leftovers of a day with attendance: removed only with the checkbox, unless `locked`. */
  attendanceDayRemovals: Array<{ slot: RegenSlot; protect: RegenProtectReason[]; locked: boolean }>;
  /** S6: sessions written shorter than the template asks (`truncated`), and
   *  template sessions with no block left in their day (`unplaced`: not created). */
  fit: {
    truncated: Array<{
      slot: RegenSlot;
      requestedMinutes: number;
      actualMinutes: number;
      belowMinimum: boolean;
    }>;
    unplaced: Array<{ date: string; topic: string; requestedMinutes: number | null }>;
  };
  /** Template sessions NOT added: their day already has attendance. */
  skippedOnAttendanceDate: RegenSlot[];
}

export interface TextbookDto {
  id: string;
  title: string;
  authors: string[];
  publisher?: string;
  year?: number;
  isbn?: string;
  language: 'es' | 'en' | 'pt' | 'other';
  category: 'required' | 'complementary';
  defaultPagesRequired?: number;
  notes?: string;
}

export interface CourseTemplateDto {
  id: string;
  tenantId: string;
  title: string;
  code?: string;
  version: number;
  // Aggregate is loaded as-is from Firestore; consumers cast specific shapes.
  description: Record<string, unknown>;
  institutionalFramework?: Record<string, unknown>;
  objectives: Record<string, unknown>;
  philosophy: unknown[];
  grading: Record<string, unknown>;
  readings: Record<string, unknown>;
  sessionFormat: Record<string, unknown>;
  modules: unknown[];
  assignments: unknown[];
  attendancePolicy?: Record<string, unknown>;
  conductRules?: Record<string, unknown>;
  latePolicy?: Record<string, unknown>;
  anchorVerse?: Record<string, unknown>;
  biblicalVersion?: string;
}

export interface SyllabusDto {
  id: string;
  sectionId: string;
  templateId: string;
  templateVersion: number;
  instructor: { personId?: string; name: string; credentials?: string };
  termRange: { startDate: string; endDate: string };
  modality: 'in-person' | 'online' | 'hybrid';
  biblicalVersion: string;
  materializedModuleIds: string[];
  materializedPageIds: string[];
  materializedSessionIds: string[];
  pdfStoragePath?: string;
  pdfGeneratedAt?: string;
}

export interface CalendarEventDto {
  type: 'session' | 'assignment';
  id: string;
  sectionId: string;
  date: string;
  startTime?: string;
  endTime?: string;
  title: string;
  scriptureRefs?: { book: string; chapter: number; verses?: string }[];
  moduleId?: string;
}

export type Weekday =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

export interface SyllabusDatePreviewRowDto {
  templateModuleId: string;
  moduleName: string;
  moduleIndex: number;
  dayIndex: number;
  weekIndex: number;
  weekday: Weekday;
  computedDate: string;
  alreadyOverridden: boolean;
  existingOverrideDate?: string;
}

export interface AssignmentDatePreviewRowDto {
  templateAssignmentId: string;
  name: string;
  weekOffset: number;
  computedDate: string;
  alreadyOverridden: boolean;
  existingDate?: string;
}

export interface ModuleSessionDayDto {
  date: string;
  dateOverridden: boolean;
  sessions: {
    id: string;
    startTime: string;
    endTime: string;
    topic: string;
    topicOverridden: boolean;
  }[];
}

export interface SectionSessionGroupDto {
  date: string;
  moduleId: string;
  moduleTitle: string;
  dateOverridden: boolean;
  modality: 'in-person' | 'online' | 'hybrid';
  attendanceTaken: boolean;
  sessions: {
    id: string;
    startTime: string;
    endTime: string;
    topic: string;
    topicOverridden: boolean;
  }[];
}

export const courseTemplateApi = {
  // Textbooks
  createTextbook: async (data: {
    tenantId: string;
    title: string;
    authors: string[];
    publisher?: string;
    year?: number;
    isbn?: string;
    language?: 'es' | 'en' | 'pt' | 'other';
    category?: 'required' | 'complementary';
    defaultPagesRequired?: number;
    notes?: string;
  }): Promise<{ id: string }> => {
    const fn = callable<typeof data, { id: string }>('createTextbook');
    return (await fn(data)).data;
  },
  updateTextbook: async (data: {
    tenantId: string;
    textbookId: string;
    updates: Partial<Omit<TextbookDto, 'id'>>;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('updateTextbook');
    return (await fn(data)).data;
  },
  deleteTextbook: async (data: {
    tenantId: string;
    textbookId: string;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('deleteTextbook');
    return (await fn(data)).data;
  },

  // Templates
  createTemplate: async (data: {
    tenantId: string;
    title: string;
    code?: string;
    courseId?: string;
    /** Provenance — set by the import flow so the eventual template records its origin. */
    source?: {
      type: 'manual' | 'pdf-import' | 'duplicated';
      importedFrom?: string;
      /** ISO 8601 string — backend `z.coerce.date()` parses to Date. Avoid sending a Date
       *  instance through httpsCallable; if it ever becomes Invalid, the SDK throws
       *  "Invalid time value" during JSON serialization before the request leaves. */
      importedAt?: string;
      importedBy?: string;
      extractionModel?: string;
    };
  }): Promise<{ id: string }> => {
    const fn = callable<typeof data, { id: string }>('createCourseTemplate');
    return (await fn(data)).data;
  },

  duplicateTemplate: async (data: {
    tenantId: string;
    sourceTemplateId: string;
  }): Promise<{ id: string }> => {
    const fn = callable<typeof data, { id: string }>('duplicateCourseTemplate');
    return (await fn(data)).data;
  },

  // AI Import — extract a draft template from an uploaded PDF

  pingClaude: async (): Promise<{ ok: boolean; model: string; reply: string }> => {
    const fn = callable<Record<string, never>, { ok: boolean; model: string; reply: string }>(
      'pingClaude',
    );
    return (await fn({})).data;
  },
  updateTemplate: async (data: {
    tenantId: string;
    templateId: string;
    patch: Record<string, unknown>;
  }): Promise<{ version: number; unchanged?: true }> => {
    const fn = callable<typeof data, { version: number; unchanged?: true }>('updateCourseTemplate');
    return (await fn(data)).data;
  },
  /** S3: la copia de antes del último guardado que cambió algo, si hay. */
  getPrevious: async (data: {
    tenantId: string;
    templateId: string;
  }): Promise<{ previous: { version: number; savedAt: string; savedBy: string } | null }> => {
    const fn = callable<
      typeof data,
      { previous: { version: number; savedAt: string; savedBy: string } | null }
    >('getCourseTemplatePrevious');
    return (await fn(data)).data;
  },
  /** S3: vuelve a esa copia como un guardado nuevo (versión +1). */
  restorePrevious: async (data: {
    tenantId: string;
    templateId: string;
  }): Promise<{ version: number; restoredFrom: number }> => {
    const fn = callable<typeof data, { version: number; restoredFrom: number }>(
      'restoreCourseTemplatePrevious',
    );
    return (await fn(data)).data;
  },
  deleteTemplate: async (data: {
    tenantId: string;
    templateId: string;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('deleteCourseTemplate');
    return (await fn(data)).data;
  },

  /** S5: what applying the template again would do to the section's sessions. */
  previewRegeneration: async (data: {
    tenantId: string;
    templateId: string;
    sectionId: string;
    termRange: { startDate: string; endDate: string };
    moduleDates?: { templateModuleId: string; dayIndex: number; date: string }[];
  }): Promise<SyllabusRegenerationPreviewDto> => {
    const fn = callable<typeof data, SyllabusRegenerationPreviewDto>('previewSyllabusRegeneration');
    return (await fn(data)).data;
  },

  // Generator
  generateSyllabus: async (data: {
    tenantId: string;
    templateId: string;
    sectionId: string;
    instructor: { personId?: string; name: string; credentials?: string };
    termRange: { startDate: string; endDate: string };
    modality?: 'in-person' | 'online' | 'hybrid';
    biblicalVersion?: string;
    /** Per-day date overrides supplied by the wizard preview step. */
    moduleDates?: { templateModuleId: string; dayIndex: number; date: string }[];
    /** Per-assignment due-date overrides supplied by the wizard step 3. */
    assignmentDates?: { templateAssignmentId: string; date: string }[];
    /** S5: also touch protected sessions (attendance, edited in the section). */
    confirmProtected?: boolean;
    /** S6·2: generate even with sessions cut below half or that do not fit. */
    confirmFitIssues?: boolean;
  }): Promise<{
    syllabusId: string;
    modulesCreated: number;
    modulesUpdated: number;
    itemsCreated: number;
    pagesCreated: number;
    pagesUpdated: number;
    /** Canonical pages a human had edited; the re-sync left them as they were. */
    pagesKeptOverridden: string[];
    sessionsCreated: number;
    sessionsUpdated: number;
    sessionsRemoved: number;
    sessionsKeptProtected?: number;
    sessionsKeyTextsUpdated?: number;
    sessionsRemovedOnAttendanceDay?: number;
    sessionsSkippedOnAttendanceDate?: number;
    assignmentsCreated: number;
    assignmentsUpdated: number;
  }> => {
    const fn = callable<typeof data, never>('generateSyllabus');
    const res = await fn(data);
    return res.data as unknown as {
      syllabusId: string;
      modulesCreated: number;
      modulesUpdated: number;
      itemsCreated: number;
      pagesCreated: number;
      pagesUpdated: number;
      pagesKeptOverridden: string[];
      sessionsCreated: number;
      sessionsUpdated: number;
      sessionsRemoved: number;
      assignmentsCreated: number;
      assignmentsUpdated: number;
    };
  },

  // Calendar
  getSectionCalendar: async (data: {
    tenantId: string;
    sectionId: string;
    fromDate: string;
    toDate: string;
  }): Promise<{ items: CalendarEventDto[] }> => {
    const fn = callable<typeof data, { items: CalendarEventDto[] }>('getSectionCalendar');
    return (await fn(data)).data;
  },

  // Preview syllabus dates (wizard step 2)
  previewSyllabusDates: async (data: {
    tenantId: string;
    templateId: string;
    sectionId: string;
    termStart: string;
  }): Promise<{
    rows: SyllabusDatePreviewRowDto[];
    assignmentRows: AssignmentDatePreviewRowDto[];
  }> => {
    const fn = callable<
      typeof data,
      { rows: SyllabusDatePreviewRowDto[]; assignmentRows: AssignmentDatePreviewRowDto[] }
    >('previewSyllabusDates');
    return (await fn(data)).data;
  },

  // Module sessions (LMS admin date editor)
  listModuleSessions: async (data: {
    tenantId: string;
    moduleId: string;
  }): Promise<{ days: ModuleSessionDayDto[] }> => {
    const fn = callable<typeof data, { days: ModuleSessionDayDto[] }>('listModuleSessions');
    return (await fn(data)).data;
  },
  updateModuleDayDate: async (data: {
    tenantId: string;
    moduleId: string;
    oldDate: string;
    newDate: string;
  }): Promise<{ moved: number }> => {
    const fn = callable<typeof data, { moved: number }>('updateModuleDayDate');
    return (await fn(data)).data;
  },
  listSectionSessions: async (data: {
    tenantId: string;
    sectionId: string;
  }): Promise<{ groups: SectionSessionGroupDto[] }> => {
    const fn = callable<typeof data, { groups: SectionSessionGroupDto[] }>('listSectionSessions');
    return (await fn(data)).data;
  },
  editSession: async (data: {
    tenantId: string;
    sessionId: string;
    topic?: string;
    startTime?: string;
    endTime?: string;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('editSession');
    return (await fn(data)).data;
  },
  moveSessionDate: async (data: {
    tenantId: string;
    sessionId: string;
    newDate: string;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('moveSessionDate');
    return (await fn(data)).data;
  },
  removeSession: async (data: {
    tenantId: string;
    sessionId: string;
    restore?: boolean;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('removeSession');
    return (await fn(data)).data;
  },
  addSession: async (data: {
    tenantId: string;
    sectionId: string;
    moduleId: string;
    date: string;
    startTime: string;
    endTime: string;
    topic: string;
  }): Promise<{ id: string }> => {
    const fn = callable<typeof data, { id: string }>('addSession');
    return (await fn(data)).data;
  },
  moveSessionToModule: async (data: {
    tenantId: string;
    sessionId: string;
    targetModuleId: string;
  }): Promise<{ success: boolean }> => {
    const fn = callable<typeof data, { success: boolean }>('moveSessionToModule');
    return (await fn(data)).data;
  },
  moveDayToModule: async (data: {
    tenantId: string;
    sourceModuleId: string;
    date: string;
    targetModuleId: string;
  }): Promise<{ moved: number }> => {
    const fn = callable<typeof data, { moved: number }>('moveDayToModule');
    return (await fn(data)).data;
  },

  // Student syllabus (read-only)
  getStudentCourseSyllabus: async (data: {
    tenantId: string;
    sectionId: string;
    requestingStudentId?: string;
  }): Promise<StudentSyllabusDto> => {
    const fn = callable<typeof data, StudentSyllabusDto>('getStudentCourseSyllabus');
    return (await fn(data)).data;
  },

  // Staff-facing composed syllabus (no enrollment gate) for the /curso tab
  getSectionSyllabus: async (data: {
    tenantId: string;
    sectionId: string;
  }): Promise<StudentSyllabusDto> => {
    const fn = callable<typeof data, StudentSyllabusDto>('getSectionSyllabus');
    return (await fn(data)).data;
  },

  // F2: edit (value) or revert (value: null) one whitelisted syllabus field of
  // ONE section — stored as a section override, never a template write.
  setSyllabusContentOverride: async (data: {
    tenantId: string;
    sectionId: string;
    // The shared whitelist, not a hand-written copy: a new path added there is
    // writable here without touching this file.
    path: SyllabusContentOverridePath;
    value: unknown;
  }): Promise<SetSyllabusOverrideResult> => {
    const fn = callable<typeof data, SetSyllabusOverrideResult>('setSyllabusContentOverride');
    return (await fn(data)).data;
  },
};

/** `reweight` only for `grading.components`: how the task weights followed. */
export interface SetSyllabusOverrideResult {
  success: true;
  pageUpdated: boolean;
  reweight?: { reweighted: number; zeroed: number; withoutTasks: string[] };
}

export type SyllabusOverridePath = Parameters<
  typeof courseTemplateApi.setSyllabusContentOverride
>[0]['path'];

// ─── F5: propuestas de cambio a la plantilla ────────────────────────────────

export interface SyllabusProposalDto {
  id: string;
  tenantId: string;
  templateId: string;
  sectionId: string;
  sectionLabel: string;
  path: string;
  kind: 'content' | 'module';
  proposedValue: unknown;
  currentValue: unknown;
  proposedByUid: string;
  proposedByName: string;
  status: 'submitted' | 'approved' | 'rejected' | 'superseded';
  submittedAt: string;
  decision?: {
    decision: 'approved' | 'rejected';
    observations: string;
    decidedByUid: string;
    decidedAt: string;
  };
  updatedAt: string;
}

export const syllabusProposalApi = {
  /** El profesor propone que un cambio de SU sección entre a la plantilla. */
  propose: async (data: {
    tenantId: string;
    sectionId: string;
    templateId: string;
    sectionLabel: string;
    actorName: string;
    path: string;
    value: unknown;
  }): Promise<{ id: string }> => {
    const fn = callable<typeof data, { id: string }>('proposeSyllabusChange');
    return (await fn(data)).data;
  },

  list: async (data: {
    tenantId: string;
    status?: 'submitted' | 'approved' | 'rejected' | 'superseded';
    templateId?: string;
  }): Promise<{ proposals: SyllabusProposalDto[] }> => {
    const fn = callable<typeof data, { proposals: SyllabusProposalDto[] }>('listSyllabusProposals');
    return (await fn(data)).data;
  },

  decide: async (data: {
    tenantId: string;
    proposalId: string;
    decision: 'approved' | 'rejected';
    observations: string;
  }): Promise<{ status: string; templateVersion?: number }> => {
    const fn = callable<typeof data, { status: string; templateVersion?: number }>(
      'decideSyllabusProposal',
    );
    return (await fn(data)).data;
  },
};

export interface StudentSyllabusDto {
  template: Record<string, unknown> & {
    id: string;
    title: string;
    code?: string;
    description: Record<string, unknown>;
    objectives: Record<string, unknown>;
    philosophy: unknown[];
    grading: Record<string, unknown>;
    readings: Record<string, unknown>;
    sessionFormat: Record<string, unknown>;
    modules: unknown[];
    assignments: unknown[];
    latePolicy?: Record<string, unknown>;
    anchorVerse?: Record<string, unknown>;
    biblicalVersion?: string;
  };
  syllabus: {
    instructor: { personId?: string; name: string; credentials?: string };
    termRange: { startDate: string; endDate: string };
    modality: 'in-person' | 'online' | 'hybrid';
    biblicalVersion: string;
  } | null;
  textbooks: {
    id: string;
    title: string;
    authors: string[];
    publisher?: string;
    year?: number;
    language?: string;
    category?: string;
  }[];
  moduleSchedule: {
    moduleId: string;
    sessions: {
      date: string;
      startTime: string;
      endTime: string;
      topic: string;
      scriptureRefs?: string;
    }[];
  }[];
  deadlines: { week: number; date: string; entry: string }[];
  /** Weight of each live task and its evaluation (staff coherence warning). */
  taskWeights?: { gradeComponentId?: string; weight: number }[];
  tenantName: string;
  documentLogoUrl?: string;
  mission?: { text: string; scripture?: string };
  programName?: string;
  programPurpose?: Record<string, unknown>;
  /**
   * Entrega 1 (hueco 8): frozen transversal layers captured at section creation.
   * Present → render these verbatim and SKIP live composition (no retroactive
   * catalog edits). Absent → legacy section; fall back to live composition.
   * Dates arrive as ISO strings (callable serialization).
   */
  transversalSnapshot?: {
    mission?: { text: string; scripture?: string };
    program?: {
      name?: string;
      purpose?: string;
      objectives: { id: string; code?: string; text: string }[];
      philosophy: { id: string; title: string; description: string }[];
    };
    department?: {
      name?: string;
      purpose?: string;
      objectives: { id: string; code?: string; text: string }[];
    };
    courseObjectives: { id: string; code?: string; text: string }[];
    rubrics: {
      id: string;
      title: string;
      criteria: {
        id: string;
        label: string;
        description: string;
        weight: number;
        levels?: { id: string; name: string; description: string }[];
      }[];
    }[];
    snapshotAt: string;
    sourceVersions: {
      templateVersion: number;
      tenantUpdatedAt?: string;
      programUpdatedAt?: string;
      departmentUpdatedAt?: string;
      courseUpdatedAt?: string;
    };
  };
  /** F2: per-section content overrides (path → value). Effective content =
   *  override ?? composed ?? template. */
  sectionOverrides?: Record<string, unknown>;
}
